/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box,
  VStack,
  Text,
  useToast,
  Divider,
  Spinner,
  Center,
  HStack,
  Card,
  CardBody,
  SimpleGrid,
  Button,
  Input,
  Heading,
  Icon
} from '@chakra-ui/react';
import { Progress } from '@chakra-ui/react';
import { FiFilter } from 'react-icons/fi';
import { useExportReceivedBooksStaffMutation } from '@partiu/api/dashboard/hooks.ts';
import { IReceivedBookStaffListResponse } from '@partiu/api/dashboard/types.ts';
import { BookCard, EmptyState } from '../components';
import { isMagazineType } from '../utils.ts';
import validateCPF from '../../../utils/CPFValidator.ts';
import { downloadExcel, CSVColumn } from '@partiu/ui/utils.ts';

const months: { [key: string]: string } = {
  '0': 'Janeiro',
  '1': 'Fevereiro',
  '2': 'Março',
  '3': 'Abril',
  '4': 'Maio',
  '5': 'Junho',
  '6': 'Julho',
  '7': 'Agosto',
  '8': 'Setembro',
  '9': 'Outubro',
  '10': 'Novembro',
  '11': 'Dezembro',
};

// Função para formatar números com separadores de milhares
const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('pt-BR').format(num);
};

function StatBox({
  label,
  value,
  suffix,
  scheme,
}: {
  label: string;
  value: number;
  suffix: string;
  scheme: string;
}) {
  return (
    <Box
      textAlign="center"
      px={2}
      py={3}
      borderRadius="md"
      bg={`${scheme}.50`}
      border="1px solid"
      borderColor={`${scheme}.100`}
      minW={0}
      overflow="hidden"
    >
      <Text
        fontSize="xs"
        color={`${scheme}.600`}
        fontWeight="semibold"
        mb={2}
        textTransform="uppercase"
        letterSpacing="wide"
        noOfLines={1}
      >
        {label}
      </Text>
      <Text
        color={`${scheme}.600`}
        fontSize="md"
        fontWeight="bold"
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
        lineHeight="1.2"
        mb={2}
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {formatNumber(value)}
      </Text>
      <Text fontSize="xs" color={`${scheme}.500`} noOfLines={1}>
        {suffix}
      </Text>
    </Box>
  );
}

export function CPFFilterTab() {
  const toast = useToast();

  const [document, setDocument] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [searchResponse, setSearchResponse] = useState<IReceivedBookStaffListResponse | null>(null);
  const [lastSearchedDocument, setLastSearchedDocument] = useState<string>('');

  const searchData = searchResponse?.results ?? null;

  // Mutation para buscar dados
  const searchMutation = useExportReceivedBooksStaffMutation({
    onSuccess: (response) => {
      setSearchResponse(response);
      setHasSearched(true);
      toast({
        title: 'Busca concluída',
        description: `Encontrados ${response?.statistics?.totalDelivered ?? response?.results?.length ?? 0} títulos para o CPF: ${document}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: () => {
      toast({
        title: 'Erro na busca',
        description: 'Não foi possível buscar os títulos. Tente novamente.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  });

  /**
   * Estatísticas vêm do bloco `statistics` da API — é ele que o backend agrega
   * sobre o total de registros. Recontar no cliente diverge sempre que a
   * resposta vier paginada. A contagem local fica só como fallback.
   */
  const statistics = useMemo(() => {
    const rows: any[] = searchResponse?.results ?? [];
    const apiStats = searchResponse?.statistics;

    const local = { book: 0, audiobook: 0, magazine: 0 };
    const periods = new Set<string>();

    rows.forEach((row: any) => {
      periods.add(`${row.yearReference}-${row.monthReference}`);

      const type = row.book?.type;
      if (!type) return;

      if (type === 'book') local.book++;
      else if (type === 'audiobook') local.audiobook++;
      else if (isMagazineType(type)) local.magazine++;
    });

    return {
      total: apiStats?.totalDelivered ?? rows.length,
      periods: apiStats?.totalPeriods ?? periods.size,
      ebooks: apiStats?.totalEbooks ?? local.book,
      audiobooks: apiStats?.totalAudiobooks ?? local.audiobook,
      magazines: apiStats?.totalMagazines ?? local.magazine,
    };
  }, [searchResponse]);

  // Agrupar livros por mês e ano
  const groupedBooks = useMemo(() => {
    if (!searchData) return {};

    const grouped: { [key: string]: any[] } = {};

    searchData.forEach((book: any) => {
      const month = book.monthReference;
      const year = book.yearReference;
      const key = `${year}-${month}`;

      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(book);
    });

    // Ordenar por ano e mês (mais recente primeiro)
    const sortedKeys = Object.keys(grouped).sort((a, b) => {
      const [yearA, monthA] = a.split('-');
      const [yearB, monthB] = b.split('-');

      if (yearA !== yearB) {
        return parseInt(yearB) - parseInt(yearA);
      }
      return parseInt(monthB) - parseInt(monthA);
    });

    const sortedGrouped: { [key: string]: any[] } = {};
    sortedKeys.forEach(key => {
      sortedGrouped[key] = grouped[key];
    });

    return sortedGrouped;
  }, [searchData]);

  // Sem lista infinita: manter agrupado; cada grupo terá uma faixa horizontal com scroll


  const handleSearch = useCallback(() => {
    if (!document.trim()) {
      toast({
        title: 'CPF obrigatório',
        description: 'Por favor, digite um CPF para buscar',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Resetar dados e executar mutation
    setSearchResponse(null);

    // Executar mutation
    searchMutation.mutate({
      document: document.replaceAll('.', '').replaceAll('-', ''),
      month_reference: '',
      year_reference: '',
      book: '',
      pagination: 'False'
    });
  }, [document, toast, searchMutation]);

  const handleExportExcel = useCallback(async () => {
    if (!searchData || !Array.isArray(searchData) || searchData.length === 0) {
      toast({ title: 'Sem dados', description: 'Realize uma busca antes de exportar.', status: 'info' });
      return;
    }

    const booksHeaders: CSVColumn[] = [
      { field: 'document', headerName: 'CPF' },
      { field: 'yearReference', headerName: 'Ano' },
      {
        field: 'monthReference',
        headerName: 'Mês',
        valueFormatter: (v: any) => (typeof v !== 'undefined' ? months[String(v)] : '')
      },
      {
        field: 'bookType',
        headerName: 'Tipo'
      },
      {
        field: 'bookTitle',
        headerName: 'Título'
      },
      { field: 'author', headerName: 'Autor' },
      { field: 'isbn', headerName: 'ISBN' },
      { field: 'description', headerName: 'Descrição' },
      {
        field: 'createdAt',
        headerName: 'Data',
        valueFormatter: (v: any) => (v ? new Date(v).toLocaleString('pt-BR') : '')
      },
    ];

    const booksRows = (searchData || []).map((r: any) => ({
      ...r,
      bookTitle: r?.book?.title || r?.title || '',
      bookType: r?.book?.type || r?.type || '',
      isbn: r?.book?.isbn || '',
      author: r?.book?.author || ''
    }));

    const statsHeaders: CSVColumn[] = [
      { field: 'metric', headerName: 'Métrica' },
      { field: 'value', headerName: 'Valor' }
    ];

    const statsRows = [
      { metric: 'CPF', value: document.replace(/\D/g, '').replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4') },
      { metric: 'Total de títulos', value: statistics.total },
      { metric: 'Períodos (meses)', value: statistics.periods },
      { metric: 'Ebooks', value: statistics.ebooks },
      { metric: 'Revistas', value: statistics.magazines },
      { metric: 'Audiobooks', value: statistics.audiobooks },
    ];

    await downloadExcel(`relatorio-cpf-${document.replace(/\D/g, '')}`, [
      { name: 'Livros', rows: booksRows, headers: booksHeaders },
      { name: 'Estatísticas', rows: statsRows, headers: statsHeaders },
    ]);

    toast({ title: 'Exportado', description: 'Arquivo Excel gerado com sucesso.', status: 'success' });
  }, [searchData, toast, statistics, document]);

  // Auto disparo da busca quando CPF é válido
  useEffect(() => {
    const numeric = document.replace(/\D/g, '');
    if (
      numeric.length === 11 &&
      validateCPF(numeric) &&
      !searchMutation.isLoading &&
      lastSearchedDocument !== numeric
    ) {
      setLastSearchedDocument(numeric);
      handleSearch();
    }
  }, [document, handleSearch, lastSearchedDocument, searchMutation.isLoading]);

  // Limpar resultados ao editar CPF incompleto (melhor fluxo para nova busca)
  useEffect(() => {
    const numeric = document.replace(/\D/g, '');
    if (numeric.length < 11) {
      setHasSearched(false);
      setSearchResponse(null);
      if (lastSearchedDocument) {
        setLastSearchedDocument('');
      }
    }
  }, [document, lastSearchedDocument]);



  return (
    <Box p={4}>
      {searchMutation.isLoading && (
        <Progress size="xs" isIndeterminate position="sticky" top={0} zIndex={10} mb={2} />
      )}
      <VStack spacing={6} align="stretch">
        {/* Filtro por CPF */}
        <Box
          bg="gray.50"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="lg"
          p={4}
        >
          <HStack mb={3} spacing={2}>
            <Icon as={FiFilter} color="blue.500" />
            <Heading size="sm" color="gray.700">
              Filtros
            </Heading>
          </HStack>
          <HStack spacing={2} alignItems="center" flexWrap="wrap">
            <Input
              placeholder="Insira um CPF"
              value={document}
              onChange={(e) => setDocument(e.target.value.replace(/\D/g, ''))}
              maxLength={11}
              width={{ base: '100%', lg: '220px' }}
              bg="white"
              variant="filled"
            />
            {searchMutation.isLoading && <Spinner size="sm" color="blue.500" />}
          </HStack>
          <Text fontSize="xs" color="gray.500" mt={2}>
            A busca é realizada automaticamente ao digitar um CPF válido.
          </Text>
        </Box>

        {/* Estatísticas */}
        {hasSearched && searchData && (
          <Card
            bg="white"
            border="none"
            shadow="lg"
            borderRadius="xl"
            overflow="hidden"
            position="relative"
            _before={{
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              bgGradient: 'linear(to-r, blue.400, purple.400, pink.400)',
            }}
          >
            <CardBody p={{ base: 4, md: 6 }}>
              <VStack spacing={4}>
                <Box textAlign="center">
                  <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="bold" color="gray.800" mb={1}>
                    📊 Estatísticas do CPF: {document.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4')}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    Resumo dos títulos recebidos por este usuário
                  </Text>
                </Box>

                <HStack justify="flex-end" w="100%">
                  <Button size="sm" colorScheme="blue" variant="solid" onClick={handleExportExcel}>
                    Exportar Excel
                  </Button>
                </HStack>

                <SimpleGrid minChildWidth="150px" spacing={3} w="100%">
                  <StatBox
                    label="Total"
                    value={statistics.total}
                    suffix="títulos"
                    scheme="blue"
                  />
                  <StatBox
                    label="Período"
                    value={statistics.periods}
                    suffix="meses"
                    scheme="green"
                  />
                  <StatBox
                    label="📚 Ebooks"
                    value={statistics.ebooks}
                    suffix="entregues"
                    scheme="red"
                  />
                  <StatBox
                    label="🎧 Audiobooks"
                    value={statistics.audiobooks}
                    suffix="entregues"
                    scheme="teal"
                  />
                  <StatBox
                    label="📰 Revistas"
                    value={statistics.magazines}
                    suffix="entregues"
                    scheme="yellow"
                  />
                </SimpleGrid>
              </VStack>
            </CardBody>
          </Card>
        )}

        {/* Resultados agrupados com rolagem horizontal por mês/ano */}
        {searchMutation.isLoading ? (
          <Center p={8}>
            <Spinner size="lg" color="blue.500" />
          </Center>
        ) : hasSearched && Object.keys(groupedBooks).length > 0 ? (
          <VStack spacing={6} align="stretch">
            {Object.entries(groupedBooks).map(([key, books]) => {
              const [year, month] = key.split('-');
              return (
                <Box key={key}>
                  <Text fontSize="xl" fontWeight="bold" color="gray.700" mb={4}>
                    {months[month]} de {year} ({formatNumber(books.length)} Título{books.length !== 1 ? 's' : ''})
                  </Text>

                  <Box
                    overflowX="auto"
                    pb={2}
                    css={{
                      '&::-webkit-scrollbar': { height: '8px' },
                      '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '4px' },
                      '&::-webkit-scrollbar-thumb': { background: '#c1c1c1', borderRadius: '4px' },
                      '&::-webkit-scrollbar-thumb:hover': { background: '#a8a8a8' },
                    }}
                  >
                    <HStack spacing={4} align="stretch" minW="max-content">
                      {books
                        .sort((a: any, b: any) => {
                          const aType = (a?.book?.type ?? a?.type) as string | undefined;
                          const bType = (b?.book?.type ?? b?.type) as string | undefined;

                          const typeOrder: Record<string, number> = {
                            book: 1,
                            magazine: 2,
                            'magazine-partiu': 2,
                            audiobook: 3,
                          };
                          const aOrder = aType ? typeOrder[aType] ?? 999 : 999;
                          const bOrder = bType ? typeOrder[bType] ?? 999 : 999;

                          if (aOrder !== bOrder) return aOrder - bOrder;

                          // Tie-breaker by title when same type
                          const aTitle = (a?.book?.title ?? a?.title ?? '') as string;
                          const bTitle = (b?.book?.title ?? b?.title ?? '') as string;
                          return aTitle.localeCompare(bTitle);
                        })
                        .map((book: any) => (
                          <Box key={`${book.id}-${book.createdAt}`} flexShrink={0}>
                            <BookCard
                              book={book.book || book}
                              size="sm"
                              showAuthor={false}
                              showPages={false}
                              showDate={true}
                              showInfo={true}
                              date={book.createdAt}
                              showHash={true}
                              hashCode={book.hashCode || book.id}
                              description={book.description}
                            />
                          </Box>
                        ))}
                    </HStack>
                  </Box>

                  <Divider />
                </Box>
              );
            })}
          </VStack>
        ) : hasSearched ? (
          <EmptyState
            type="search"
            title="Nenhum título encontrado para este CPF"
            actionText="Verifique se o CPF está correto ou tente outro"
          />
        ) : (
          <EmptyState
            type="search"
            title="Digite um CPF para buscar os títulos recebidos"
            actionText="Os títulos serão agrupados por mês e ano"
          />
        )}
      </VStack>
    </Box>
  );
}
